import React, { useEffect, useState } from 'react';
import {
  PdsBox,
  PdsCard,
  PdsMantineText,
  PdsMantineIconButton,
  PdsMantineButton,
  PdsChevronRightSmIcon,
  PdsChevronDownSmIcon,
} from '@podium-design-system/react-components';

/**
 * Tab-level Condense / Expand (comfortable shows full content; compact collapses to titles + expand affordances).
 */
export function InvestigationDensityToolbar({ density, onDensityChange, children }) {
  const compact = density === 'compact';
  return (
    <PdsBox
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      wrap="wrap"
      gap="sm"
      style={{ marginBottom: 12, paddingBottom: 8, borderBottom: '1px solid #e8eaed' }}
    >
      <PdsMantineButton
        rank="outline"
        color="neutral"
        size="xs"
        onClick={() => onDensityChange(compact ? 'comfortable' : 'compact')}
        leadingIcon={compact ? <PdsChevronDownSmIcon /> : <PdsChevronRightSmIcon />}
        aria-pressed={compact}
      >
        {compact ? 'Expand view' : 'Condense view'}
      </PdsMantineButton>
      {children}
    </PdsBox>
  );
}

/**
 * Major SecurityTrails block: in compact mode only title row + chevron until expanded.
 * Optional accordion props (dense mode only): multiple sections may be open; `expandedAccordionIds` tracks which `accordionId`s are expanded.
 */
export function InvestigationSectionCard({
  dense,
  title,
  icon: Icon,
  count,
  children,
  accordionId,
  expandedAccordionIds,
  onAccordionToggle,
}) {
  const accordionControlled =
    Boolean(dense) &&
    typeof accordionId === 'string' &&
    expandedAccordionIds instanceof Set &&
    typeof onAccordionToggle === 'function';

  const [internalOpen, setInternalOpen] = useState(!dense);

  useEffect(() => {
    if (!accordionControlled) {
      setInternalOpen(!dense);
    }
  }, [dense, accordionControlled]);

  const open = accordionControlled ? expandedAccordionIds.has(accordionId) : internalOpen;

  const toggle = () => {
    if (accordionControlled) {
      onAccordionToggle(accordionId);
    } else {
      setInternalOpen((o) => !o);
    }
  };

  if (!dense) {
    return (
      <div data-section-id={accordionId || undefined}>
      <PdsCard border borderRadius="sm" padding="sm" direction="column" gap="md" color="neutral" contrast="low">
        <PdsBox direction="row" alignItems="center" gap="xs">
          {Icon && <Icon color="action" size="sm" />}
          <PdsMantineText type="textline" fontWeight="strong" component="span" className="argus-type-card-title">
            {title}
            {count != null ? ` (${count})` : ''}
          </PdsMantineText>
        </PdsBox>
        {children}
      </PdsCard>
      </div>
    );
  }

  return (
    <div data-section-id={accordionId || undefined}>
    <PdsCard border borderRadius="sm" padding="sm" direction="column" gap="md" color="neutral" contrast="low">
      <PdsBox
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        gap="sm"
        style={{ cursor: 'pointer', userSelect: 'none' }}
        onClick={toggle}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggle();
          }
        }}
        role="button"
        tabIndex={0}
        aria-expanded={open}
      >
        <PdsBox direction="row" alignItems="center" gap="xs" style={{ minWidth: 0, flex: 1 }}>
          {Icon && <Icon color="action" size="sm" />}
          <PdsMantineText
            type="textline"
            fontWeight="strong"
            component="span"
            className="argus-type-card-title"
            style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            {title}
            {count != null ? ` (${count})` : ''}
          </PdsMantineText>
        </PdsBox>
        <PdsMantineIconButton
          size="sm"
          rank="ghost"
          color="neutral"
          icon={open ? <PdsChevronDownSmIcon /> : <PdsChevronRightSmIcon />}
          aria-label={open ? 'Collapse section' : 'Expand section'}
        />
      </PdsBox>
      {open ? children : null}
    </PdsCard>
    </div>
  );
}

/**
 * Single row in a list (IP, MX, subdomain, etc.): compact shows one title line + expand.
 * variant "row": comfortable layout matches IP/MX rows (body left, actions right).
 * variant "column": comfortable layout is a simple stacked card (e.g. Historical WHOIS).
 */
export function InvestigationDenseRow({
  dense,
  titleLine,
  /** When set with titleValue, renders label (Source Sans) + value (mono) instead of a single mono titleLine */
  titlePrefix,
  titleValue,
  /** Allow long DNS strings (e.g. TXT) to wrap in compact rows instead of ellipsis-only */
  titleWordBreak = false,
  actions,
  children,
  variant = 'row',
  /**
   * When `dense` (compact rows), start with the detail panel expanded.
   * Use for e.g. IP rows that already have geo/ASN so analysts see signal immediately.
   * Omit or false: start collapsed (default).
   */
  initialExpanded = false,
  /** When false, dense rows always show children with no per-row chevron (e.g. domain + IP). */
  collapsible = true,
}) {
  const [open, setOpen] = useState(() => (dense ? Boolean(initialExpanded) : true));
  const detailChildren = React.Children.toArray(children).filter(Boolean);
  const hasDetails = detailChildren.length > 0;

  useEffect(() => {
    setOpen(dense ? Boolean(initialExpanded) : true);
  }, [dense, initialExpanded]);

  const titleStyle = {
    flex: 1,
    minWidth: 0,
    overflow: titleWordBreak ? 'visible' : 'hidden',
    textOverflow: titleWordBreak ? 'clip' : 'ellipsis',
    whiteSpace: titleWordBreak ? 'normal' : 'nowrap',
    wordBreak: titleWordBreak ? 'break-word' : undefined,
  };

  if (!dense || !collapsible) {
    if (variant === 'row') {
      return (
        <PdsCard border borderRadius="sm" padding="xs" direction="row" justifyContent="space-between" alignItems="center">
          <PdsBox direction="column" gap="sm" flex="1">
            {detailChildren}
          </PdsBox>
          {actions}
        </PdsCard>
      );
    }
    return (
      <PdsCard border borderRadius="sm" padding="xs" direction="column" gap="sm">
        {detailChildren}
      </PdsCard>
    );
  }

  const splitTitle = titlePrefix != null && String(titlePrefix).length > 0 && titleValue != null;
  const titleAttr = splitTitle ? `${titlePrefix} ${titleValue}` : typeof titleLine === 'string' ? titleLine : undefined;

  if (!hasDetails) {
    return (
      <PdsCard border borderRadius="sm" padding="xs" direction="column" gap="xs">
        <PdsBox direction="row" alignItems="flex-start" justifyContent="space-between" gap="sm" wrap="nowrap">
          {splitTitle ? (
            <PdsBox direction="row" alignItems="baseline" gap="xs" style={{ flex: 1, minWidth: 0 }}>
              <span className="argus-type-label" style={{ flexShrink: 0 }}>
                {titlePrefix}
              </span>
              <PdsMantineText type="textline" component="span" className="argus-data" style={titleStyle} title={titleAttr}>
                {titleValue}
              </PdsMantineText>
            </PdsBox>
          ) : (
            <PdsMantineText type="textline" component="span" className="argus-data" style={titleStyle} title={titleAttr}>
              {titleLine}
            </PdsMantineText>
          )}
          {actions}
        </PdsBox>
      </PdsCard>
    );
  }

  return (
    <PdsCard border borderRadius="sm" padding="xs" direction="column" gap="xs">
      <PdsBox
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        gap="sm"
        wrap="nowrap"
        style={{ cursor: 'pointer', userSelect: 'none' }}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpen((o) => !o);
          }
        }}
        role="button"
        tabIndex={0}
        aria-expanded={open}
      >
        {splitTitle ? (
          <PdsBox
            direction="row"
            alignItems="baseline"
            gap="xs"
            style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}
          >
            <span className="argus-type-label" style={{ flexShrink: 0 }}>
              {titlePrefix}
            </span>
            <PdsMantineText
              type="textline"
              component="span"
              className="argus-data"
              style={titleStyle}
              title={titleAttr}
            >
              {titleValue}
            </PdsMantineText>
          </PdsBox>
        ) : (
          <PdsMantineText
            type="textline"
            component="span"
            className="argus-data"
            style={titleStyle}
            title={titleAttr}
          >
            {titleLine}
          </PdsMantineText>
        )}
        <PdsBox direction="row" alignItems="center" gap="xs" style={{ flexShrink: 0 }}>
          {actions}
          <PdsMantineIconButton
            size="sm"
            rank="ghost"
            color="neutral"
            icon={open ? <PdsChevronDownSmIcon /> : <PdsChevronRightSmIcon />}
            onClick={(e) => {
              e.stopPropagation();
              setOpen((o) => !o);
            }}
            aria-label={open ? 'Collapse row' : 'Expand row'}
          />
        </PdsBox>
      </PdsBox>
      {open ? (
        <PdsBox direction="column" gap="xs">
          {detailChildren}
        </PdsBox>
      ) : null}
    </PdsCard>
  );
}
